import * as React from "react";
import { If } from "@components/if";
import { MaybeError } from "@components/maybe-error";
import { Timeline } from "@components/timeline";
import {IRecordDTO} from "dtos";
import { PoweredByFooter } from "./powered-by-footer";
import {useLoadingState} from "@hooks/use-loading-state";
import {isApiError, ProxyService} from "@modules/api/api";
import { getHeaderClass } from "./get-header-class";

interface UnloadedProps {
    loaded: false
}

interface LoadedProps {
    loaded: true
    records: IRecordDTO[]
    orderName: string
}

type Props = (UnloadedProps | LoadedProps) & {
    shopUrl: string;
    themeColor: string;
    orderShopifyId: number
    allowSearch: boolean;
    onNavigateToSearch: () => void;
    service: ProxyService
}

interface State {
    records: IRecordDTO[]
    orderName: string
    dateCreated: Date;
}

export function TimelineSection(props: Props): JSX.Element {
    const [state, setState] = React.useState<State>({
        records: props.loaded ? props.records : [],
        orderName: props.loaded ? props.orderName : "",
        dateCreated: new Date()
    });
    const [{loading, error}, setLoadingState] = useLoadingState(props.loaded === false);

    const backToSearch = (event: React.MouseEvent<any>) => {
        event.preventDefault();

        props.onNavigateToSearch();
    };

    React.useEffect(() => {
        if (!props.loaded) {
            loadTimeline(props.orderShopifyId, true);    
        }
    }, [props.loaded, props.orderShopifyId])

	async function loadTimeline(orderShopifyId: number, ignoreLoadingCheck?: boolean) {
        if (loading && !ignoreLoadingCheck) return;

        setLoadingState(true);

        try {
            const data = await props.service.GetOrderAsync(orderShopifyId);

            setState({
                dateCreated: new Date(data.dateCreated),
                orderName: data.displayId,
                records: data.records
            })
            setLoadingState(false);
        } catch (e: any) {
            console.error("Failed to load timeline data for order " + orderShopifyId, e);

            setLoadingState(isApiError(e) && e.HumanReadable
                ? e.message
                : "Something went wrong and your order could not be loaded. Please try again."
            );
        }
	}

    function retryLoadTimeline(e: React.FormEvent) {
        e.preventDefault();
        loadTimeline(props.orderShopifyId);
    }

    const headerClass = getHeaderClass(props.shopUrl);

    if (loading) {
        return (
			<section className="m-col-18-24">
				<div className="box" style={{ borderTop: "6px solid " + props.themeColor }}>
					<form className="panel active">
						<div className={headerClass}>
							<h4>{`Loading updates for ${state.orderName || "order"}.`}</h4>
						</div>
						<div className="body">
							<div className="text-center" style={{ padding: "100px 0" }}>
								<i className="fa fa-spin fa-spinner fa-5x" style={{ color: props.themeColor }} />
							</div>
						</div>
						<div className="footer">
							<div className="ms-row vc">
								<div className="m-col-12-24">
                                    <PoweredByFooter shopUrl={props.shopUrl} />
                                </div>
							</div>
						</div>
					</form>
				</div>
			</section>
        )
    }

    return (
        <section className="m-col-24-24">
            <div className="box" style={{ borderTop: "6px solid " + props.themeColor }}>
                <form className="panel active">
                    <div className="header">
                        <h4>{`Latest updates for order ${state.orderName}.`}</h4>
                    </div>
                    <div className="body">
                        <Timeline 
                            ArchiveDate={null} 
                            History={state.records} 
                            DateCreated={state.dateCreated} 
                            ShowSource={false} 
                        />
                        <MaybeError error={error} />
                    </div>
                    <div className="footer">
                        <div className="ms-row vc">
                            <div className="m-col-12-24">
                                <PoweredByFooter shopUrl={props.shopUrl} />
                            </div>
                            <div className="m-col-12-24 text-right">
                                <If condition={error !== null}>
                                    <button className="btn" onClick={retryLoadTimeline}>
                                        {"Retry"}
                                    </button>
                                </If>
                                <If condition={error === null && props.allowSearch}>
                                    <button 
                                        className="btn btn-primary" 
                                        type="button" 
                                        onClick={backToSearch}
                                    >
                                        {"Search for another order."}
                                    </button>
                                </If>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
}
